import { Box, Icon, Table, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { ReconciliationSkeleton } from 'modules/banking/components/ReconcileTransaction/ReconciliationSkeleton'
import {
  ReconciliationItem,
  ReconciliationSource,
} from 'modules/banking/hooks/useReconciliationForm'

import { Reconciliations } from './Reconciliations'
import { SummaryDateCell } from './SummaryDateCell'
import { SummaryDescriptionCell } from './SummaryDescriptionCell'
import { SummaryMoneyCell } from './SummaryMoneyCell'
import { SummaryTotalsRow } from './SummaryTotalsRow'

interface Props {
  source: ReconciliationSource | null | undefined
  reconciliations: ReconciliationItem[]
  onRemove: (item: ReconciliationItem) => void
  loading: boolean
  locale: string
}

export const ReconciliationSummary = ({
  source,
  reconciliations,
  onRemove,
  loading,
  locale,
}: Props) => {
  const EmptyState = () => (
    <Table.Row>
      <Table.Cell colSpan={5}>
        <Box
          height="full"
          width="full"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          style={{ height: '149px' }}
        >
          <Icon.Large icon="workflow" color="grey600" />
          <Text.Header2>
            {i18n.t('banking.reconciliations.reconciliation_summary_empty_state.title')}
          </Text.Header2>
          <Text.Normal color="grey800">
            {i18n.t('banking.reconciliations.reconciliation_summary_empty_state.description')}
          </Text.Normal>
        </Box>
      </Table.Cell>
    </Table.Row>
  )

  const optionsBottom =
    !loading && source
      ? () => <SummaryTotalsRow source={source} locale={locale} reconciliations={reconciliations} />
      : undefined

  const showEmptyState = !loading && !source && reconciliations.length === 0

  return (
    <div data-testid="reconciliation-summary">
      <Table
        style={{ flex: '1', overflowY: 'auto', height: 'auto' }}
        optionsBottom={optionsBottom}
        bottomSticky
        optionsBottomBackground="grey000White"
        autoLayout
      >
        <Table.Head>
          <Table.Row>
            <Table.HeadCell background="grey000White" width={108} paddingX="s4" ellipsis>
              <Box style={{ paddingLeft: '20px' }}>
                {i18n.t('finance.reconciliation.summary.columns.date')}
              </Box>
            </Table.HeadCell>
            <Table.HeadCell paddingX="s4" background="grey000White" ellipsis>
              {i18n.t('finance.reconciliation.summary.columns.description')}
            </Table.HeadCell>
            <Table.HeadCell
              background="grey000White"
              width={100}
              paddingX="s4"
              ellipsis
              justifyContent="flexEnd"
            >
              {i18n.t('finance.reconciliation.summary.columns.debit')}
            </Table.HeadCell>
            <Table.HeadCell
              background="grey000White"
              width={100}
              paddingX="s4"
              ellipsis
              justifyContent="flexEnd"
            >
              {i18n.t('finance.reconciliation.summary.columns.credit')}
            </Table.HeadCell>
            <Table.HeadCell
              background="grey000White"
              paddingX="s6"
              style={{ width: '1px', whiteSpace: 'nowrap' }}
            >
              <Box overflowY="auto" style={{ scrollbarGutter: 'stable', width: '50px' }} />
            </Table.HeadCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {!showEmptyState && (
            <Table.Row>
              <Table.Cell noBorder paddingX="s0" paddingY="s0" colSpan={5}>
                <Box
                  width="full"
                  overflowY="auto"
                  style={{ scrollbarGutter: 'stable', height: '136px' }}
                >
                  <Table style={{ flex: '1' }} optionsBottomBackground="grey000White" hideBorder>
                    {loading && <ReconciliationSkeleton />}
                    {!loading && source && (
                      <Table.Row>
                        <SummaryDateCell date={source.date} />
                        <SummaryDescriptionCell
                          description={source.description}
                          secondaryDescription={source.secondaryDescription}
                        />
                        <SummaryMoneyCell
                          visible={source.type == 'debit'}
                          amount={source.amountCentsToReconcile}
                          currency={source.currency}
                          locale={source.locale}
                        />
                        <SummaryMoneyCell
                          visible={source.type == 'credit'}
                          amount={source.amountCentsToReconcile}
                          currency={source.currency}
                          locale={source.locale}
                        />
                        <Table.Cell noBorder paddingX="s0" paddingY="s2" width={50}></Table.Cell>
                      </Table.Row>
                    )}
                    {!loading && source && reconciliations.length > 0 && (
                      <Reconciliations reconciliations={reconciliations} onRemove={onRemove} />
                    )}
                  </Table>
                </Box>
              </Table.Cell>
            </Table.Row>
          )}
          {showEmptyState && <EmptyState />}
        </Table.Body>
      </Table>
    </div>
  )
}
